import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

/**
 * La visite guidée, proposée une fois le premier tableau de bord affiché.
 *
 * ## Pourquoi à ce moment-là
 *
 * Elle arrive quand l'utilisateur a enfin quelque chose à regarder : ses propres chiffres.
 * Une visite menée sur un écran vide ne montrerait que des cadres, et c'est précisément ce que
 * le parcours d'installation existe pour éviter.
 *
 * Elle se **propose** et ne s'impose pas : qui vient de brancher son compte veut d'abord voir
 * ses données. « Plus tard » referme sans rien entamer.
 *
 * ## Une seule fois, puis à la demande
 *
 * Elle ne s'ouvre d'elle-même **qu'une fois**, au premier tableau de bord qui suit
 * l'installation. `visiteGuideeVueAt` est posé à l'affichage, pas à la fermeture : quelle que
 * soit la façon dont on referme — « Plus tard », la croix, Échap, le voile —, la proposition
 * ne revient pas. Une invitation qui réapparaît à chaque connexion est une nuisance, et c'est
 * le reproche qu'on lui faisait.
 *
 * Ce qui n'est possible que parce qu'elle reste **rejouable à volonté**, depuis Aide et
 * assistance › Ressources. `avecInvitation={false}` démarre alors directement à la première
 * étape : qui vient de la demander n'a pas besoin qu'on lui propose.
 *
 * ## Les cibles sont déclarées, pas devinées
 *
 * Chaque étape vise un `data-visite="…"` posé sur l'élément réel. Un sélecteur de classe aurait
 * suivi la feuille de style et se serait rompu au premier restylage, sans que rien ne le signale
 * — la bulle aurait simplement pointé le vide.
 *
 * ⚠️ **Une cible absente ne casse pas la visite.** Les éléments visés n'existent pas sur tous
 * les écrans ni pour tous les rôles : le filtre client disparaît quand on n'a qu'un client, le
 * bouton d'analyse n'est pas sur toutes les vues. Dans ce cas la bulle se centre et l'étape se
 * lit quand même, plutôt que de désigner un coin d'écran au hasard.
 */

/** Les étapes, dans l'ordre. `cible` renvoie à un attribut `data-visite` de l'interface. */
export const ETAPES = [
  {
    cible: 'periode',
    titre: 'La période analysée',
    texte: "Tout ce que vous voyez à l'écran dépend de ce réglage. Vous pouvez aussi comparer à la période précédente ou à l'an dernier — l'écart s'affiche alors à côté de chaque chiffre."
  },
  {
    cible: 'filtre-client',
    titre: 'Vos clients, vos sites',
    texte: "Ce filtre borne l'application entière à un client. En dessous, le fil d'Ariane vous situe : régie, compte, puis la vue choisie."
  },
  {
    cible: 'analyse-ia',
    titre: "L'analyse par l'IA",
    texte: "Elle commente les performances affichées : ce qui progresse, ce qui décroche, et ce qu'il y aurait à regarder. Elle part de la période et des filtres en cours."
  },
  {
    cible: 'outils',
    titre: 'La boîte à outils',
    texte: "Diagnostic de compte, mots-clés, flux produits, suivi de positions : des outils qui travaillent sur les comptes que vous venez de connecter."
  },
  {
    cible: 'mcp',
    titre: 'Vos données dans Claude',
    texte: "Le serveur MCP ouvre vos données à Claude : vous interrogez vos campagnes en langage courant, sans passer par un écran."
  }
];

/** Où placer la bulle par rapport à la cible, sans jamais déborder de la fenêtre. */
function positionner(rect, taille) {
  const marge = 12;
  const { innerWidth: L, innerHeight: H } = window;
  if (!rect) {
    return { top: Math.max(marge, H / 2 - taille.h / 2), left: Math.max(marge, L / 2 - taille.l / 2) };
  }
  // Sous la cible si la place y est, au-dessus sinon.
  const dessous = rect.bottom + marge;
  const top = dessous + taille.h < H ? dessous : Math.max(marge, rect.top - taille.h - marge);
  const left = Math.min(Math.max(marge, rect.left), L - taille.l - marge);
  return { top, left };
}

function VisiteGuidee({ onTerminer, onPlusTard, avecInvitation = true }) {
  const [invitationVisible, setInvitationVisible] = useState(avecInvitation);
  const [etape, setEtape] = useState(0);
  const [rect, setRect] = useState(null);

  const TAILLE = { l: 340, h: 200 };
  const courante = ETAPES[etape];

  /** Suivre la cible : elle bouge au défilement comme au redimensionnement. */
  const mesurer = useCallback(() => {
    if (invitationVisible) return;
    const el = document.querySelector(`[data-visite="${courante.cible}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    // Un élément masqué a une boîte vide : on le traite comme absent.
    setRect(r.width === 0 && r.height === 0 ? null : r);
  }, [courante, invitationVisible]);

  useEffect(() => {
    mesurer();
    window.addEventListener('resize', mesurer);
    window.addEventListener('scroll', mesurer, true);
    return () => {
      window.removeEventListener('resize', mesurer);
      window.removeEventListener('scroll', mesurer, true);
    };
  }, [mesurer]);

  // Les flèches et Échap : une visite se parcourt au clavier aussi.
  useEffect(() => {
    if (invitationVisible) return undefined;
    const auClavier = (e) => {
      if (e.key === 'Escape') onTerminer?.();
      if (e.key === 'ArrowRight') setEtape((n) => Math.min(n + 1, ETAPES.length - 1));
      if (e.key === 'ArrowLeft') setEtape((n) => Math.max(n - 1, 0));
    };
    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [invitationVisible, onTerminer]);

  if (invitationVisible) {
    return ReactDOM.createPortal(
      <div style={styles.invitation}>
        <div style={{ fontSize: '15px', fontWeight: 600, color: '#1f2937', marginBottom: '6px' }}>
          Découvrir Kokpit en deux minutes ?
        </div>
        <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>
          Cinq repères pour savoir où regarder, maintenant que vos données sont là.
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button style={styles.boutonPrincipal} onClick={() => setInvitationVisible(false)}>
            Commencer
          </button>
          <button style={styles.boutonDiscret} onClick={() => onPlusTard?.()}>
            Plus tard
          </button>
        </div>
      </div>,
      document.body
    );
  }

  const { top, left } = positionner(rect, TAILLE);
  const dernier = etape === ETAPES.length - 1;

  return ReactDOM.createPortal(
    <>
      {/* Le voile, percé à l'endroit de la cible : c'est lui qui désigne. */}
      <div style={styles.voile} onClick={() => onTerminer?.()} />
      {rect && (
        <div
          style={{
            position: 'fixed',
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            borderRadius: '10px',
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
            border: '2px solid #FAF9F5',
            pointerEvents: 'none',
            zIndex: 10000
          }}
        />
      )}

      <div style={{ ...styles.bulle, top, left, width: TAILLE.l }}>
        <button style={styles.croix} onClick={() => onTerminer?.()} aria-label="Fermer la visite">
          <X size={16} />
        </button>

        <div style={{ fontSize: '15px', fontWeight: 600, color: '#1f2937', marginBottom: '6px' }}>
          {courante.titre}
        </div>
        <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280', lineHeight: 1.6 }}>
          {courante.texte}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '5px', flex: 1 }}>
            {ETAPES.map((e, i) => (
              <span
                key={e.cible}
                style={{
                  width: '6px', height: '6px', borderRadius: '50%',
                  background: i === etape ? '#1a1a1a' : '#d1d5db'
                }}
              />
            ))}
          </div>
          {etape > 0 && (
            <button style={styles.boutonDiscret} onClick={() => setEtape(etape - 1)}>Précédent</button>
          )}
          <button
            style={styles.boutonPrincipal}
            onClick={() => (dernier ? onTerminer?.() : setEtape(etape + 1))}
          >
            {dernier ? 'Terminer' : 'Suivant'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

const styles = {
  invitation: {
    position: 'fixed', right: '24px', bottom: '24px', zIndex: 10001,
    width: '340px', background: 'white', borderRadius: '12px', padding: '18px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.18)', border: '1px solid #e5e7eb'
  },
  voile: { position: 'fixed', inset: 0, zIndex: 9999 },
  bulle: {
    position: 'fixed', zIndex: 10001, background: 'white', borderRadius: '12px',
    padding: '18px', boxShadow: '0 10px 40px rgba(0,0,0,0.22)', border: '1px solid #e5e7eb'
  },
  croix: {
    position: 'absolute', top: '10px', right: '10px', background: 'none',
    border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px'
  },
  boutonPrincipal: {
    padding: '8px 16px', background: '#1a1a1a', color: 'white', border: 'none',
    borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer'
  },
  boutonDiscret: {
    padding: '8px 12px', background: 'white', border: '1px solid #d1d5db',
    borderRadius: '8px', fontSize: '13px', color: '#374151', cursor: 'pointer'
  }
};

export default VisiteGuidee;
